/**
 * One status block of the orders screen: collapsible header with the quick
 * filters and the pagination, then one row per order with the "details" cell,
 * the "mark as" cell, the status dropdown and the print cell. The details
 * dialog is a separate step of the migration (see migration.md).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faCaretDown,
  faCaretRight,
  faExclamationTriangle,
  faInfoCircle,
  faPrint,
} from '@fortawesome/free-solid-svg-icons';
import { PLATFORM, ROLES, type Platform, type Role } from '@/shared/constants';
import cartonGrey from '@/assets/images/cartonGrey.jpg';
import cartonReceived from '@/assets/images/cartonReceived.jpg';
import cartonShipped from '@/assets/images/cartonShipped.jpg';
import type { OrderPage } from './api';
import { ORDER_TYPES, STATUS_COLORS } from './order-types';
import {
  applyQuickFilter,
  canPrint,
  formatLocalDate,
  formatOrderTime,
  incrementStatusId,
  isOrderValid,
  midPages,
  nextStatusIds,
  quickFilters,
  showMarkAsButton,
  textParts,
  updateStatusAllowed,
  type EnrichedOrder,
} from './logic';
import { kebabCase } from '@/shared/lib/kebab-case';
import { partsStatusChangeAllowed } from './dms/dms-logic';

const filterIcons = import.meta.glob<string>(
  '@/assets/images/dms_filters/*.png',
  { eager: true, import: 'default', query: '?url' },
);
const filterIcon = (name: string): string =>
  filterIcons[`/src/assets/images/dms_filters/${name}.png`] ?? '';

export interface OrdersBlockProps {
  platform: Platform;
  role: Role;
  statusId: number;
  items: EnrichedOrder[];
  pagination: OrderPage;
  collapsed: boolean;
  updatingStatus: Record<string, boolean>;
  onToggleCollapse: () => void;
  onPageChange: (page: number) => void;
  onUpdateStatus: (order: EnrichedOrder, target: number) => void;
  onOpenDetails: (order: EnrichedOrder) => void;
  onPrintStickers: (order: EnrichedOrder) => void;
}

const cartonTitle = (item: EnrichedOrder) =>
  `RECEIVED at Blois: ${formatOrderTime(item, 'MMM D, YYYY', 'receivedDateBlois')}\nSHIPPED from Blois: ${formatOrderTime(item, 'MMM D, YYYY', 'shipmentDateBlois')}`;

const Carton = ({ item }: { item: EnrichedOrder }) => {
  if (
    ![4, 5, 6].includes(item.customisationStatusId) ||
    item.workshopType !== 'central'
  )
    return null;
  if (!item.receivedDateBlois && !item.shipmentDateBlois)
    return <img src={cartonGrey} className="cartonImg" alt="" />;
  return (
    <img
      src={item.shipmentDateBlois ? cartonShipped : cartonReceived}
      className="cartonImg cp"
      alt={item.shipmentDateBlois ? 'Shipped from Blois' : 'Received at Blois'}
      title={cartonTitle(item)}
    />
  );
};

export function OrdersBlock({
  platform,
  role,
  statusId,
  items,
  pagination,
  collapsed,
  updatingStatus,
  onToggleCollapse,
  onPageChange,
  onUpdateStatus,
  onOpenDetails,
  onPrintStickers,
}: Readonly<OrdersBlockProps>) {
  const { t } = useTranslation();
  const isDms = platform === PLATFORM.DMS;
  const isWorkshop = role === ROLES.WORKSHOP;
  const showUserData = !isWorkshop;
  const [quickFilter, setQuickFilter] = useState('all');
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLTableCellElement>(null);
  const title = ORDER_TYPES[statusId];
  const statusSlug = kebabCase(title ?? String(statusId));
  const filters = quickFilters(platform, statusId);
  const rows = applyQuickFilter(platform, statusId, items, quickFilter);
  const totalPages = pagination.totalPages ?? 0;
  const page = pagination.page ?? 1;

  useEffect(() => {
    if (!openMenu) return;
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openMenu]);

  const pageItem = (
    key: string,
    label: ReactNode,
    target: number | null,
    { active = false, disabled = false, ariaLabel = '', testId = '' } = {},
  ) => (
    <li
      key={key}
      className={`page-item ${active ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
    >
      <button
        type="button"
        className="page-link"
        disabled={disabled || target === null}
        aria-label={ariaLabel || undefined}
        aria-current={active ? 'page' : undefined}
        data-testid={testId || undefined}
        onClick={() => {
          if (target !== null) onPageChange(target);
        }}
      >
        {label}
      </button>
    </li>
  );

  const statusMenu = (item: EnrichedOrder, dms: boolean) => (
    <td
      ref={openMenu === item.id ? menuRef : undefined}
      className="dropdown-toggle pointer-cursor relative text-center"
      style={{
        width: '3%',
        background: 'gray',
        maxWidth: 44,
        ...(dms ? { borderLeft: '1px solid #dee2e6' } : {}),
      }}
    >
      <button
        type="button"
        className="status-menu-toggle"
        aria-haspopup="menu"
        aria-expanded={openMenu === item.id}
        aria-label={`Change status of ${item.displayId ?? ''}`}
        data-testid="order-row-status-button"
        onClick={() => setOpenMenu(openMenu === item.id ? null : item.id)}
      />
      {openMenu === item.id && (
        <div className="dropdown-menu show" role="menu">
          {nextStatusIds(platform, role, item.customisationStatusId).map(
            (number) => (
              <button
                key={number}
                type="button"
                role="menuitem"
                className="dropdown-item"
                style={{ color: STATUS_COLORS[number] }}
                data-testid="order-row-status-option"
                data-value={number}
                disabled={
                  dms
                    ? !partsStatusChangeAllowed(item, number)
                    : !updateStatusAllowed(item, number)
                }
                onClick={() => {
                  setOpenMenu(null);
                  onUpdateStatus(item, number);
                }}
              >
                <div
                  className="status-point"
                  style={{ background: STATUS_COLORS[number] }}
                />
                <div
                  className="no-select"
                  style={{ color: STATUS_COLORS[number] }}
                >
                  {ORDER_TYPES[number]}
                </div>
              </button>
            ),
          )}
        </div>
      )}
    </td>
  );

  const markAsCell = (item: EnrichedOrder) => {
    const target =
      item.customisationStatusId === 7
        ? 1
        : incrementStatusId(item.customisationStatusId);
    const label = `${t('orders.markAs')} ${ORDER_TYPES[target]}`;
    const qualityBadge = (item.product as { qualityIssue?: unknown } | null)
      ?.qualityIssue ? (
      <div>
        <span className="badge badge-pill badge-danger">!</span>
      </div>
    ) : null;
    const show = showMarkAsButton(
      platform,
      role,
      item,
      !!updatingStatus[item.id],
    );
    if (!show)
      return (
        <td
          className="pointer text-center"
          style={{ width: '9%', background: 'black', color: 'white' }}
        >
          &nbsp;{qualityBadge}
        </td>
      );
    if (!isOrderValid(item))
      return (
        <td
          className="pointer text-center"
          style={{ width: '9%', background: 'black', color: 'white' }}
          title="PLEASE FILL-IN ALL INFORMATION IN THE ORDER DETAILS"
        >
          {label}&nbsp;
          <span className="badge badge-pill badge-warning rounded-circle">
            !
          </span>
          {qualityBadge}
        </td>
      );
    return (
      <td
        className="pointer text-center"
        style={{ width: '9%', background: 'black', color: 'white', padding: 0 }}
      >
        <button
          type="button"
          className="mark-as"
          data-testid="order-row-mark-as-button"
          onClick={() => onUpdateStatus(item, target)}
        >
          {label}&nbsp;{qualityBadge}
        </button>
      </td>
    );
  };

  return (
    <div className="card mb-2" data-testid={`order-block-${statusSlug}`}>
      <div
        className={`left-black-bar sticky sticky-header-${items[0]?.customisationStatusId ?? ''}`}
      >
        <h5 className="orders-title my-2 mb-0">
          <button
            type="button"
            className="btn text-muted text-left"
            aria-expanded={!collapsed}
            data-testid={`order-block-${statusSlug}-toggle-button`}
            onClick={onToggleCollapse}
          >
            {title} {t('orders.orders')}
            <FontAwesomeIcon
              className="ml-2"
              icon={collapsed ? faCaretRight : faCaretDown}
            />
          </button>
          {filters.length > 0 && (
            <div className="surmesure-filters">
              {filters.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className="quick-filter"
                  aria-label={`Filter ${filter}`}
                  aria-pressed={quickFilter === filter}
                  data-testid={`order-filter-${kebabCase(filter)}`}
                  data-value={filter}
                  onClick={() => setQuickFilter(filter)}
                >
                  <img
                    src={filterIcon(
                      `${filter}${quickFilter === filter ? '' : '_unselected'}`,
                    )}
                    alt=""
                  />
                </button>
              ))}
            </div>
          )}
          {totalPages > 1 && (
            <ul className="pagination orders-pagination">
              {pageItem(
                'prev',
                <span aria-hidden="true">&laquo;</span>,
                page - 1,
                {
                  disabled: !pagination.hasPrevPage,
                  ariaLabel: 'Previous',
                  testId: 'order-block-prev-button',
                },
              )}
              {page >= 5 &&
                totalPages >= 8 && [
                  pageItem('first', '1', 1, {
                    testId: 'order-block-page-1-button',
                  }),
                  pageItem('gap-start', '...', null, { disabled: true }),
                ]}
              {midPages(pagination).map((i) =>
                pageItem(`p${i}`, String(i), i, {
                  active: i === page,
                  testId: `order-block-page-${i}-button`,
                }),
              )}
              {totalPages - page >= 4 &&
                totalPages >= 8 && [
                  pageItem('gap-end', '...', null, { disabled: true }),
                  pageItem('last', String(totalPages), totalPages, {
                    testId: `order-block-page-${totalPages}-button`,
                  }),
                ]}
              {pageItem(
                'next',
                <span aria-hidden="true">&raquo;</span>,
                page + 1,
                {
                  disabled: !pagination.hasNextPage,
                  ariaLabel: 'Next',
                  testId: 'order-block-next-button',
                },
              )}
            </ul>
          )}
          {pagination.totalDocs != null && (
            <div
              className="order-count items-center"
              data-testid={`order-block-${statusSlug}-count`}
            >
              {pagination.totalDocs} {title} {t('orders.orders')}
            </div>
          )}
        </h5>
      </div>
      <div hidden={collapsed}>
        <table
          className="table table-borderless mb-0"
          style={{ tableLayout: 'fixed' }}
        >
          <tbody>
            {rows.map((item) => (
              <tr
                key={item.id}
                className={
                  item.priority === 'true' || item.priority === true
                    ? 'left-red-bar order'
                    : 'left-white-bar order'
                }
              >
                {!isDms && (
                  <>
                    <th
                      style={{ width: '7%', maxWidth: 72 }}
                      scope="row"
                      data-testid="order-row-id"
                    >
                      {item.displayId}
                      <strong className="id-subtext">{item.storeName}</strong>
                      <span className="id-subtext">
                        {formatOrderTime(item, 'MMM D, YYYY')}
                      </span>
                    </th>
                    {!isWorkshop && (
                      <td
                        style={{ width: '6%' }}
                        title={showUserData ? (item.lastName ?? '') : 'XXX'}
                        data-testid="order-row-last-name"
                      >
                        {showUserData ? item.lastName : 'XXX'}
                      </td>
                    )}
                    {!isWorkshop && (
                      <td
                        style={{ width: '6%' }}
                        title={showUserData ? (item.firstName ?? '') : 'XXX'}
                        data-testid="order-row-first-name"
                      >
                        {showUserData ? item.firstName : 'XXX'}
                      </td>
                    )}
                    {!isWorkshop && (
                      <td
                        style={{ width: '6%' }}
                        title={item.sellerName ?? ''}
                        data-testid="order-row-seller"
                      >
                        {item.sellerName}
                      </td>
                    )}
                    <td
                      style={{ width: '9%' }}
                      title={item.productName ?? ''}
                      data-testid="order-row-item"
                    >
                      {item.productName}
                    </td>
                    {isWorkshop && (
                      <td
                        style={{ width: '6%' }}
                        title={item.productReference ?? ''}
                        data-testid="order-row-product-reference"
                      >
                        {item.productReference}
                      </td>
                    )}
                    <td
                      style={{ width: '5%', whiteSpace: 'pre-wrap' }}
                      data-testid="order-row-text"
                    >
                      {textParts(item.customText).map((part, i) => (
                        <span
                          key={i}
                          style={
                            part.symbol
                              ? { fontFamily: 'DiorSymbolesCondensedBold' }
                              : undefined
                          }
                        >
                          {part.value}
                        </span>
                      ))}
                    </td>
                    {isWorkshop && (
                      <td style={{ width: '5%' }} data-testid="order-row-color">
                        {item.color?.name}
                      </td>
                    )}
                    {isWorkshop && (
                      <td style={{ width: '5%' }} data-testid="order-row-oa">
                        {item.oaCode}
                        <Carton item={item} />
                      </td>
                    )}
                    <td
                      style={{ width: '6%' }}
                      data-testid="order-row-workshop"
                    >
                      {item.workshopName}{' '}
                      {item.customizationType !== '-'
                        ? `(${item.customizationType})`
                        : ''}
                      {!isWorkshop && <Carton item={item} />}
                    </td>
                    <td
                      style={{
                        color: STATUS_COLORS[item.customisationStatusId],
                        width: '7%',
                        wordWrap: 'initial',
                      }}
                      data-testid="order-row-status"
                    >
                      {ORDER_TYPES[item.customisationStatusId]}
                      {item.onHold && (
                        <div className="text-danger">
                          <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                          {t('orders.onHold')}
                        </div>
                      )}
                    </td>
                  </>
                )}
                {isDms && (
                  <>
                    <th
                      style={{ width: '16%' }}
                      scope="row"
                      data-testid="order-row-id"
                    >
                      {item.displayId}
                      <strong className="id-subtext">{item.storeName}</strong>
                      <span className="id-subtext">
                        {formatOrderTime(item, 'MMM D, YYYY')}
                      </span>
                    </th>
                    <td
                      style={{ width: '8%' }}
                      title={showUserData ? (item.lastName ?? '') : 'XXX'}
                      data-testid="order-row-last-name"
                    >
                      {showUserData ? item.lastName : 'XXX'}
                    </td>
                    <td
                      style={{ width: '8%' }}
                      title={showUserData ? (item.firstName ?? '') : 'XXX'}
                      data-testid="order-row-first-name"
                    >
                      {showUserData ? item.firstName : 'XXX'}
                    </td>
                    <td
                      style={{ width: '5%' }}
                      title={String(item.shirtCount ?? '')}
                      data-testid="mtm-order-row-shirt-count"
                    >
                      {item.shirtCount}
                    </td>
                    <td
                      style={{ width: '5%' }}
                      title={String(
                        (item.itemsCount ?? 0) - (item.shirtCount ?? 0),
                      )}
                      data-testid="mtm-order-row-non-shirt-count"
                    >
                      {(item.itemsCount ?? 0) - (item.shirtCount ?? 0)}
                    </td>
                    <td
                      style={{ width: '7%' }}
                      title={String(item.itemsCount ?? '')}
                      data-testid="mtm-order-row-items-count"
                    >
                      {item.itemsCount}
                    </td>
                    <td
                      style={{ width: '5%' }}
                      data-testid="mtm-order-row-express"
                    >
                      {!!(item.props as { isExpress?: unknown } | null)
                        ?.isExpress && (
                        <FontAwesomeIcon icon={faBolt} aria-label="Express" />
                      )}
                    </td>
                    <td
                      style={{
                        width: '13%',
                        color: item.deliverySoon ? 'darkorange' : 'inherit',
                      }}
                      title={item.closestExpectedDeliveryDate?.toString() ?? ''}
                      data-testid="mtm-order-row-delivery-date"
                    >
                      {item.closestExpectedDeliveryDate &&
                        formatLocalDate(item.closestExpectedDeliveryDate)}
                      {(item.expectedDeliveryDates?.length ?? 0) > 1 && (
                        <span title={item.expectedDeliveryDatesStr}>
                          <FontAwesomeIcon
                            icon={faInfoCircle}
                            style={{ color: '#212529', marginLeft: 5 }}
                          />
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        color: STATUS_COLORS[item.customisationStatusId],
                        width: '7%',
                        wordWrap: 'initial',
                      }}
                      data-testid="order-row-status"
                    >
                      {ORDER_TYPES[item.customisationStatusId]}
                      {item.onHold && (
                        <div className="text-danger">
                          <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
                          {t('orders.onHold')}
                        </div>
                      )}
                    </td>
                  </>
                )}

                <td
                  className="no-select pointer-cursor text-center"
                  style={{
                    background: 'gray',
                    width: isDms ? '19%' : '6%',
                    padding: 0,
                  }}
                >
                  <button
                    type="button"
                    className="details-cell"
                    data-testid="order-row-details-button"
                    onClick={() => onOpenDetails(item)}
                  >
                    {t('orders.details')}
                  </button>
                </td>

                {isDms && role === ROLES.ADMIN && statusMenu(item, true)}

                {!isDms && (
                  <>
                    {markAsCell(item)}
                    {statusMenu(item, false)}
                    <td
                      className={`print-sticker print-sticker--${canPrint(role, item)} text-center`}
                      style={{ width: '3%', padding: 0 }}
                    >
                      <button
                        type="button"
                        className="print-cell"
                        aria-label={`Print stickers of ${item.displayId ?? ''}`}
                        data-testid="order-row-print-button"
                        disabled={!canPrint(role, item)}
                        onClick={() => onPrintStickers(item)}
                      >
                        <FontAwesomeIcon icon={faPrint} />
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
